import { Suspense, useEffect, useState } from "react";
import Loading from "../../../../common/components/ui/loading/Loading";
import { useNavigate } from "react-router-dom";
import { ISupplier, SUPPLIER_STATUS } from "../../../../types/supplier.types";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../common/components/ui/table/TableFilters";
import { getSuppliers } from "../../../../common/services/supplier.api";
import GenericTable from "../../../../common/components/ui/table/GenericTable";
import { formatDateAndTime } from "../../../../common/helpers/date.helpers";
import TableFooter from "../../../../common/components/ui/table/TableFooter";
import ModalCreateSupplier from "../modals/ModalCreateSupplier";
import ModalEditSupplier from "../modals/ModalEditSupplier";

const STATUS_LABELS = {
  [SUPPLIER_STATUS.Aprovado]: "Aprovado",
  [SUPPLIER_STATUS.Pendente]: "Pendente",
};
const headers = {
  tradingName: "Nome fantasia",
  companyName: "Razão social",
  status: "Status",
  updatedAt: "Atualizado em",
  createdAt: "Criado em",
};

function TableSuppliers() {
  const EMPTY_ACTION = { eventName: "", data: undefined };
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [suppliers, setSuppliers] = useState<ISupplier[]>([]);
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
  });

  useEffect(() => {
    async function loadSuppliers(filters, page) {
      const result = await getSuppliers({ ...filters, page });

      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }

      setSuppliers(result.data as ISupplier[]);
    }

    loadSuppliers(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  const actions = [
    {
      label: "editar",
      action: (data) =>
        setAction({ eventName: "edit", data: (data as ISupplier)._id }),
      permission: "edit:inventory-suppliers",
    },
  ];

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = () => {
    handleSearch(filters);
    setAction(EMPTY_ACTION);
  };

  const handleSearch = ({ search, status }) => {
    const newState = Object.assign({}, filters, { search, status });
    setFilters(newState);
  };
  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };
  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Fornecedores"
          placeholder="Digite o nome fantasia, o CNPJ ou a razão social."
          statusOptions={STATUS_LABELS}
          newItemLabel="Novo Fornecedor"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          onSearch={handleFilterChange}
          entity="inventory-suppliers"
          tableData={suppliers}
        />
        <GenericTable
          headers={headers}
          data={suppliers}
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={
            {
              tradingName: (tradingName, supplier) => (
                <a
                  href={`/fornecedores/${(supplier as ISupplier)._id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/fornecedores/${(supplier as ISupplier)._id}`);
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {tradingName as string}
                </a>
              ),
              updatedAt: formatDateAndTime,
              createdAt: formatDateAndTime,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="inventory-suppliers"
        />

        {!suppliers.length && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
        {isAction("create") && (
          <ModalCreateSupplier onClose={cleanActionData} />
        )}
        {isAction("edit") && (
          <ModalEditSupplier
            supplierId={action.data as string}
            onClose={cleanActionData}
          />
        )}
      </div>
    </Suspense>
  );
}

export default TableSuppliers;
